import { useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import Moment from 'react-moment';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrackingNumbers from "components/shared/TrackingNumbers";
import { clearOrder } from "reducers/order.slice";
import DownloadPdf from "components/shared/DownloadPdf";
import ItemCollapsable from './shared/ItemCollapsable';
import StatusProgressBar from "./shared/StatusProgressBar";


const OrderEntry = () => {

    const dispatch = useAppDispatch();

    const selectedOrder = useAppSelector(x => x.orders.selectedOrder);

    if (!selectedOrder) return null;

    const renderStatus = (status: string) => {
        switch (status) {
            case "Billed":
            case "Cancelled":
            case "Closed":
            case "Pending Billing":
                return (
                    <>
                        <span className="text-success fw-bold">
                            Order Shipped
                        </span>
                        <p><em>Your order is shipped. Please reference the tracking number below.</em></p>
                    </>
                );
            case "Pending Approval":
                return (
                    <>
                        <span className="text-info">
                            Order Received
                        </span>
                        <p><em>Your order have been recieved and is being review by our operations team.</em></p>
                    </>
                );
            case "Pending Fulfillment":
                return (
                    <>
                        <span className="text-info">
                            Order Being Processed
                        </span>
                        <p><em>Your order has been approved and is currenly being processed by our warehouse and lab team. It will be out in no time!</em></p>
                    </>
                );
            default:
                return status.toLowerCase().includes("partially fulfilled")
                    ?
                    <>
                        <span className="text-warning">
                            Order Partially Shipped/Back Ordered
                        </span>
                        <p><em>Your order is partially shipped or is in back order due to inventory outage.</em></p>
                    </>
                    :
                    status;
        }
    };

    const itemName = selectedOrder.itemName !== null ? selectedOrder.itemName?.includes(",") ? selectedOrder.itemName!.split(",")
        : [selectedOrder.itemName!] : ["n/a"];


    return (
        <Container className="overflow-hidden">
            <div className="d-flex">
                <Button color="link" className="text-decoration-none" onClick={() => dispatch(clearOrder())}>
                    <FontAwesomeIcon icon='arrow-left-long' className="ms-1 me-2"/>
                    Back to Orders
                </Button>
                <h2 className="my-3 text-center w-75">Order Details</h2>
            </div>
            <Card className="shadow my-2 fs-5">
                <CardBody className="text-center text-nowrap">
                    <Row className="mt-3 me-4">
                        <Col className='fw-bold'>
                            Status
                        </Col>
                    </Row>
                    <Row className="mt-3 me-4">
                        <Col>
                            <StatusProgressBar status={selectedOrder.status?.toLowerCase()} />
                        </Col>
                    </Row>
                    <Row className="mt-3 me-4">
                        <Col>
                            <div className="d-flex justify-content-center mb-4 mx-5">
                                <span>
                                    {renderStatus(selectedOrder.status || "")}
                                </span>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow my-2 fs-5">
                <CardBody className="text-center text-nowrap">
                    <Row className="mt-3 me-4">
                        <Col>
                            <div className="d-flex justify-content-between mb-4 mx-5">
                                <label className="fw-bold me-2">Sales Order</label>
                                <span>
                                    {selectedOrder.salesOrder}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between mb-4 mx-5">
                                <label className="fw-bold me-2">Date</label>
                                <span>
                                    <Moment format="MM/DD/YYYY">
                                        {selectedOrder.transactionDate}
                                    </Moment>
                                </span>
                            </div>
                        </Col>
                        <Col>
                            <div className="mb-3 mx-5 d-flex justify-content-between flex-column flex-sm-row">
                                <label className="fw-bold me-2">Purchase Order</label>
                                <span>
                                    {selectedOrder.purchaseOrder ?
                                        <DownloadPdf model={{ fileId: selectedOrder.purchaseOrder || "", fileName: selectedOrder.purchaseOrder || "", recordType: "purchaseOrder" }} fontSize="fs-5" />
                                        : "--"
                                    }
                                </span>
                            </div>
                            <div className="mb-3 mx-5 d-flex justify-content-between flex-column flex-sm-row">
                                <label className="fw-bold me-2">Invoice</label>
                                <span>
                                    {selectedOrder.invoiceNumber ?
                                        <DownloadPdf model={{
                                            fileId: selectedOrder.invoiceId?.toString() || "", fileName: selectedOrder.invoiceNumber || "", recordType: "invoice" }} fontSize="fs-5" />
                                        : "--"
                                    }
                                </span>
                            </div>
                            <div className="mb-3 mx-5 d-flex justify-content-between flex-column flex-sm-row">
                                <label className="fw-bold me-2">Packing Slip</label>
                                <span>
                                    {selectedOrder.itemFulfillmentId ?
                                        <DownloadPdf model={{ fileId: selectedOrder.itemFulfillmentId || "", fileName: selectedOrder.itemFulfillmentTranId || "", recordType: "packingSlip" }} fontSize="fs-5" />
                                        : "--"
                                    }
                                </span>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow my-2 fs-5">
                <CardBody>
                    <TrackingNumbers model={selectedOrder}/>
                </CardBody>
            </Card>
            {selectedOrder.qcNotes && (
                <MacAddressesCard qcNotes={selectedOrder.qcNotes} />
            )}
            {
                itemName?.map((item, i) => {
                    return <ItemCollapsable indice={i} key={i} itemName={item} />
                })
            }
        </Container>
    );
}

const MacAddressesCard = ({ qcNotes }: { qcNotes: string }) => {
    const [collapsed, setCollapsed] = useState<boolean>(true);

    return (
        <Card className="shadow my-2 fs-5">
            <CardBody>
                <Row className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                    <Col className="fw-bold text-nowrap ms-sm-5">
                        <label>MAC Addresses</label>
                    </Col>
                    <Col sm={2}
                        role="button"
                        tabIndex={0}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? 'Expand MAC Addresses' : 'Collapse MAC Addresses'}
                        onClick={() => setCollapsed(!collapsed)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(!collapsed); } }}
                        className="text-center bcd-pointer"
                    >
                        <FontAwesomeIcon icon={collapsed ? 'angles-down' : 'angles-up'} size="xl" className="text-info" />
                    </Col>
                </Row>
                <Card hidden={collapsed} className="shadow fs-5 fade-in">
                    <CardBody>
                        <pre className="mb-0 bcd-mac-addresses">
                            {qcNotes}
                        </pre>
                    </CardBody>
                </Card>
            </CardBody>
        </Card>
    );
};

export default OrderEntry;